import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trophy, Play, Target, Loader2, X, ChevronRight, Award, BookOpen, Share2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// PortalScorecardTab — Onglet "Jouer" du portail client
// ============================================================================
// Fonctionnalités :
// - Wizard 3 étapes : Choix tee + index → saisie carte → résultat + publier
// - Preview du Playing HCP en temps réel (WHS)
// - Leaderboard 2 modes (Brut par défaut / Net) avec toggle
// - Historique de mes parties
// - Suggestion cours automatique si score > par + seuil (configurable admin)
// - Lien direct vers le carnet de parcours PDF si configuré
// ============================================================================

export default function PortalScorecardTab({ token, onNavigateToLessons }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('home'); // home | wizard | result | leaderboard | history
  const [myScorecards, setMyScorecards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardMode, setLeaderboardMode] = useState('gross');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('year');
  const [savedResult, setSavedResult] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({ category: 'men', tee_code: '', handicap_index: '', holes: {} });
  const [playingHcpPreview, setPlayingHcpPreview] = useState(null);
  const [editingId, setEditingId] = useState(null); // iter316.58 — id de la scorecard en cours d'édition, null = création
  const [showLessonModal, setShowLessonModal] = useState(false); // iter316.60 — popup suggestion cours

  useEffect(() => {
    fetch(`${API}/api/portal/course-config`)
      .then(r => r.json())
      .then(d => { setConfig(d); setLoading(false); })
      .catch(() => setLoading(false));
    if (token) refreshMyScorecards();
  }, [token]);

  useEffect(() => {
    if (view === 'leaderboard') refreshLeaderboard();
  }, [view, leaderboardMode, leaderboardPeriod]);

  const refreshMyScorecards = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/portal/scorecards`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMyScorecards(await res.json());
    } catch {}
  };

  const refreshLeaderboard = async () => {
    try {
      const res = await fetch(`${API}/api/portal/leaderboard?mode=${leaderboardMode}&period=${leaderboardPeriod}`);
      if (res.ok) setLeaderboard(await res.json());
    } catch {}
  };

  // Preview playing handicap en temps réel
  useEffect(() => {
    if (!wizardData.tee_code || wizardData.handicap_index === '' || wizardData.handicap_index === null) {
      setPlayingHcpPreview(null);
      return;
    }
    const idx = parseFloat(wizardData.handicap_index);
    if (isNaN(idx)) { setPlayingHcpPreview(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/portal/playing-handicap?tee_code=${wizardData.tee_code}&handicap_index=${idx}`);
        if (res.ok) setPlayingHcpPreview(await res.json());
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [wizardData.tee_code, wizardData.handicap_index]);

  const startWizard = () => {
    setView('wizard');
    setWizardStep(1);
    setEditingId(null);
    const activeTees = (config?.tees || []).filter(t => t.is_active);
    // Catégorie par défaut : hommes s'il en existe, sinon la 1ère dispo
    const genders = Array.from(new Set(activeTees.map(t => (t.gender || 'men').toLowerCase())));
    const defaultCat = genders.includes('men') ? 'men' : (genders[0] || 'men');
    const firstTee = activeTees.find(t => (t.gender || 'men').toLowerCase() === defaultCat);
    setWizardData({ category: defaultCat, tee_code: firstTee?.code || '', handicap_index: '', holes: {} });
    setPlayingHcpPreview(null);
  };

  const startEdit = (sc) => {
    // Retrouver la catégorie du tee utilisé
    const activeTees = (config?.tees || []).filter(t => t.is_active);
    const teeUsed = activeTees.find(t => t.code === sc.tee_code);
    const category = (teeUsed?.gender || 'men').toLowerCase();
    // Reconstituer les scores brut par trou
    const holesMap = {};
    (sc.holes_detail || sc.holes || []).forEach(h => { holesMap[h.number] = h.gross; });
    setEditingId(sc.id);
    setWizardData({
      category,
      tee_code: sc.tee_code,
      handicap_index: String(sc.handicap_index),
      holes: holesMap,
    });
    setPlayingHcpPreview(null);
    setWizardStep(1);
    setView('wizard');
  };

  const submitScorecard = async (publish) => {
    const holesArray = (config?.holes || []).map(h => ({ number: h.number, gross: parseInt(wizardData.holes[h.number]) || 0 }));
    if (holesArray.some(h => !h.gross || h.gross < 1)) {
      toast.error('Remplissez chaque trou avant de valider');
      return;
    }
    try {
      const isEditing = !!editingId;
      const url = isEditing
        ? `${API}/api/portal/scorecards/${editingId}`
        : `${API}/api/portal/scorecards`;
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tee_code: wizardData.tee_code,
          handicap_index: parseFloat(wizardData.handicap_index),
          holes: holesArray,
          is_published: publish,
        }),
      });
      if (!res.ok) { toast.error(isEditing ? 'Erreur mise à jour' : 'Erreur enregistrement'); return; }
      const data = await res.json();
      setSavedResult(data);
      setShowLessonModal(!!data?.lesson_suggestion?.should_suggest);
      setView('result');
      setEditingId(null);
      refreshMyScorecards();
      toast.success(isEditing ? 'Partie modifiée !' : (publish ? 'Score publié !' : 'Score enregistré'));
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  const deleteScorecard = async (id) => {
    if (!window.confirm('Supprimer cette partie ?')) return;
    try {
      await fetch(`${API}/api/portal/scorecards/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      refreshMyScorecards();
      toast.success('Partie supprimée');
    } catch {}
  };

  const togglePublish = async (id, currentlyPublished) => {
    try {
      await fetch(`${API}/api/portal/scorecards/${id}/publish`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_published: !currentlyPublished }),
      });
      refreshMyScorecards();
      toast.success(!currentlyPublished ? 'Publié !' : 'Retiré du leaderboard');
    } catch {}
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!config) return <div className="p-8 text-center text-neutral-500">Configuration non chargée</div>;

  const activeTees = (config.tees || []).filter(t => t.is_active);

  // ==================== VIEW: HOME ====================
  if (view === 'home') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">🏌️ Jouer</h2>
          <p className="text-sm text-neutral-600">Enregistre ta partie, publie ton score, compare-toi aux autres membres.</p>
        </div>

        {/* CTA principaux */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-6 hover:shadow-lg transition cursor-pointer border-2 border-emerald-100" onClick={startWizard} data-testid="scorecard-play-btn">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full bg-emerald-100"><Play className="text-emerald-600" size={24} /></div>
              <div>
                <h3 className="font-bold text-lg">Nouvelle partie</h3>
                <p className="text-xs text-neutral-500">Saisis ton scorecard</p>
              </div>
            </div>
            <p className="text-xs text-neutral-600 mt-2">Calcul automatique brut / net / Stableford selon ton index.</p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition cursor-pointer border-2 border-amber-100" onClick={() => setView('leaderboard')} data-testid="scorecard-leaderboard-btn">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full bg-amber-100"><Trophy className="text-amber-600" size={24} /></div>
              <div>
                <h3 className="font-bold text-lg">Leaderboard</h3>
                <p className="text-xs text-neutral-500">Classement des membres</p>
              </div>
            </div>
            <p className="text-xs text-neutral-600 mt-2">Brut · Net · sur mois / année / all-time.</p>
          </Card>
        </div>

        {/* Carnet de parcours */}
        {config.carnet_pdf_url && (
          <Card className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-amber-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <BookOpen className="text-amber-700" size={22} />
                <div>
                  <p className="font-semibold text-sm">Carnet de parcours</p>
                  <p className="text-xs text-neutral-600">Distances, dangers, conseils sur chaque trou</p>
                </div>
              </div>
              <a href={config.carnet_pdf_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded font-semibold hover:bg-amber-700" data-testid="scorecard-carnet-link">
                Ouvrir
              </a>
            </div>
          </Card>
        )}

        {/* Règles locales */}
        {config.local_rules_pdf_url && (
          <Card className="p-4 bg-gradient-to-r from-slate-50 to-neutral-50 border-neutral-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <BookOpen className="text-slate-700" size={22} />
                <div>
                  <p className="font-semibold text-sm">Règles locales</p>
                  <p className="text-xs text-neutral-600">Spécificités du parcours à connaître</p>
                </div>
              </div>
              <a href={config.local_rules_pdf_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded font-semibold hover:bg-slate-800" data-testid="scorecard-local-rules-link">
                Ouvrir
              </a>
            </div>
          </Card>
        )}

        {/* Mon historique */}
        {myScorecards.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><Target size={18} /> Mes parties</h3>
            <div className="space-y-2">
              {myScorecards.slice(0, 5).map(sc => (
                <ScorecardRow key={sc.id} sc={sc} onDelete={deleteScorecard} onTogglePublish={togglePublish} onEdit={startEdit} />
              ))}
              {myScorecards.length > 5 && (
                <button className="text-xs text-emerald-600 hover:underline" onClick={() => setView('history')}>
                  Voir toutes ({myScorecards.length}) →
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== VIEW: WIZARD ====================
  if (view === 'wizard') {
    const canProceedStep1 = wizardData.tee_code && parseFloat(wizardData.handicap_index) >= -5 && parseFloat(wizardData.handicap_index) <= 54;
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{editingId ? 'Modifier ma partie' : 'Nouvelle partie'} · Étape {wizardStep}/2</h2>
          <button onClick={() => { setView('home'); setEditingId(null); }} className="text-neutral-500 hover:text-neutral-700"><X size={20} /></button>
        </div>

        {wizardStep === 1 && (() => {
          const CATEGORY_META = {
            men: { label: 'Hommes', icon: '♂' },
            women: { label: 'Dames', icon: '♀' },
            junior: { label: 'Jeunes', icon: '★' },
            kids: { label: 'Enfants', icon: '★' },
          };
          const availableGenders = Array.from(new Set(activeTees.map(t => (t.gender || 'men').toLowerCase())));
          const orderedCategories = ['men', 'women', 'junior', 'kids'].filter(g => availableGenders.includes(g));
          const teesForCategory = activeTees.filter(t => (t.gender || 'men').toLowerCase() === wizardData.category);
          const handleCategoryChange = (cat) => {
            const firstTee = activeTees.find(t => (t.gender || 'men').toLowerCase() === cat);
            setWizardData({ ...wizardData, category: cat, tee_code: firstTee?.code || '' });
          };
          return (
          <Card className="p-6 space-y-4">
            <div>
              <label className="text-sm font-semibold block mb-2">Catégorie</label>
              <div className="grid grid-cols-3 gap-2">
                {orderedCategories.map(cat => {
                  const meta = CATEGORY_META[cat] || { label: cat, icon: '•' };
                  const isSelected = wizardData.category === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => handleCategoryChange(cat)}
                      className={`p-2.5 rounded-lg border-2 transition-all ${isSelected ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500 ring-offset-1 shadow-sm' : 'border-neutral-200 hover:border-emerald-300 hover:bg-neutral-50'}`}
                      data-testid={`scorecard-category-${cat}`}
                    >
                      <p className={`text-xl leading-none mb-0.5 ${isSelected ? 'text-emerald-700' : 'text-neutral-500'}`}>{meta.icon}</p>
                      <p className={`text-xs font-semibold ${isSelected ? 'text-emerald-800' : ''}`}>{meta.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-2">Marque de départ</label>
              {teesForCategory.length === 0 ? (
                <p className="text-xs text-neutral-500 italic p-3 bg-neutral-50 rounded">Aucune marque configurée pour cette catégorie. Contactez l'accueil.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {teesForCategory.map(tee => {
                    const isSelected = wizardData.tee_code === tee.code;
                    const isWhite = (tee.color_hex || '').toUpperCase() === '#FFFFFF';
                    return (
                      <button
                        key={tee.code}
                        onClick={() => setWizardData({...wizardData, tee_code: tee.code})}
                        className={`relative p-3 rounded-lg border-2 transition-all ${isSelected ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500 ring-offset-1 shadow-md' : 'border-neutral-200 hover:border-emerald-300 hover:bg-neutral-50'}`}
                        data-testid={`scorecard-tee-${tee.code}`}
                      >
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold shadow">
                            ✓
                          </div>
                        )}
                        <div
                          className="w-9 h-9 rounded-full mx-auto mb-2"
                          style={{
                            backgroundColor: tee.color_hex || '#FFFFFF',
                            border: isWhite ? '2px solid #4b5563' : '2px solid rgba(0,0,0,0.15)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                          }}
                        />
                        <p className={`text-xs font-semibold ${isSelected ? 'text-emerald-800' : ''}`}>{tee.label}</p>
                        <p className="text-[10px] text-neutral-500">Slope {tee.slope} · CR {tee.cr}</p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold block mb-2">Ton index de handicap</label>
              <Input
                type="number"
                step="0.1"
                min="-5"
                max="54"
                value={wizardData.handicap_index}
                onChange={(e) => setWizardData({...wizardData, handicap_index: e.target.value})}
                placeholder="Ex : 24.5"
                className="text-lg font-semibold"
                data-testid="scorecard-handicap-input"
              />
            </div>

            {playingHcpPreview && (() => {
              const selectedTee = activeTees.find(t => t.code === wizardData.tee_code);
              const isWhite = (selectedTee?.color_hex || '').toUpperCase() === '#FFFFFF';
              return (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    {selectedTee && (
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: selectedTee.color_hex || '#FFFFFF',
                          border: isWhite ? '1.5px solid #4b5563' : '1.5px solid rgba(0,0,0,0.15)'
                        }}
                      />
                    )}
                    <p className="text-xs text-neutral-600">
                      Depuis <strong>{selectedTee?.label || ''}</strong> · Slope {selectedTee?.slope} · CR {selectedTee?.cr}
                    </p>
                  </div>
                  <p className="text-[10px] text-neutral-500">Formule WHS : Index × Slope/113 + (CR − Par)</p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-3xl font-bold text-emerald-700">{playingHcpPreview.playing_handicap}</span>
                    <span className="text-sm text-neutral-600">coups rendus sur les 18 trous</span>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end">
              <Button disabled={!canProceedStep1} onClick={() => setWizardStep(2)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="scorecard-next-step">
                Continuer <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </Card>
          );
        })()}

        {wizardStep === 2 && (
          <Card className="p-4 sm:p-6 space-y-4">
            <div className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-10 gap-2 text-center text-[10px] font-bold text-neutral-500 border-b pb-2">
              <div className="col-span-1">Trou</div>
              <div className="col-span-1">Par</div>
              <div className="col-span-1">SI</div>
              <div className="col-span-1 text-emerald-600">Rendus</div>
              <div className="col-span-2 sm:col-span-2 md:col-span-1">Brut</div>
              <div className="hidden sm:block col-span-2 md:col-span-2">Net</div>
              <div className="hidden md:block col-span-2">Stbl.Net</div>
            </div>
            {(config.holes || []).map(h => {
              const gross = parseInt(wizardData.holes[h.number]) || 0;
              const rendus = playingHcpPreview?.strokes_per_hole?.[h.number] || 0;
              const net = gross ? gross - rendus : 0;
              const stbl = net ? stablefordNet(net, h.par) : 0;
              return (
                <div key={h.number} className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-10 gap-2 items-center py-1 border-b border-neutral-100">
                  <div className="col-span-1 text-center font-semibold text-sm">{h.number}</div>
                  <div className="col-span-1 text-center text-sm">{h.par}</div>
                  <div className="col-span-1 text-center text-xs text-neutral-500">{h.stroke_index}</div>
                  <div className="col-span-1 text-center text-xs text-emerald-600">+{rendus}</div>
                  <div className="col-span-2 sm:col-span-2 md:col-span-1">
                    <Input
                      type="number"
                      min="1"
                      max="15"
                      value={wizardData.holes[h.number] || ''}
                      onChange={(e) => setWizardData({...wizardData, holes: {...wizardData.holes, [h.number]: e.target.value}})}
                      className="text-center font-bold h-8"
                      data-testid={`scorecard-hole-${h.number}`}
                    />
                  </div>
                  <div className="hidden sm:block col-span-2 md:col-span-2 text-center text-sm text-neutral-600">{gross ? net : '—'}</div>
                  <div className="hidden md:block col-span-2 text-center text-sm font-semibold text-amber-600">{gross ? stbl : '—'}</div>
                </div>
              );
            })}

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 pt-3 border-t sm:justify-between sm:items-center">
              <div className="text-sm">
                <div><span className="text-neutral-500">Brut : </span><span className="font-bold text-lg">{Object.values(wizardData.holes).reduce((s, v) => s + (parseInt(v) || 0), 0)}</span></div>
              </div>
              <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
                <Button variant="outline" onClick={() => setWizardStep(1)} data-testid="scorecard-back-step">Retour</Button>
                <Button variant="outline" onClick={() => submitScorecard(false)} data-testid="scorecard-save-private">Enregistrer sans publier</Button>
                <Button onClick={() => submitScorecard(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="scorecard-save-publish">
                  <Share2 size={14} className="mr-1" /> Publier au leaderboard
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  // ==================== VIEW: RESULT ====================
  if (view === 'result' && savedResult) {
    const overPar = savedResult.total_net - savedResult.par_total;
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold">
            <Award size={16} /> Partie enregistrée
          </div>
          <h2 className="text-2xl font-bold">{savedResult.tee_label} · Index {savedResult.handicap_index}</h2>
          <p className="text-sm text-neutral-500">Coups rendus : {savedResult.playing_handicap}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 text-center bg-blue-50 border-blue-200">
            <p className="text-xs text-neutral-600 mb-1">Score brut</p>
            <p className="text-3xl font-bold text-blue-700">{savedResult.total_gross}</p>
          </Card>
          <Card className="p-4 text-center bg-emerald-50 border-emerald-200">
            <p className="text-xs text-neutral-600 mb-1">Score net</p>
            <p className="text-3xl font-bold text-emerald-700">{savedResult.total_net}</p>
            <p className="text-xs text-neutral-500 mt-1">{overPar >= 0 ? `+${overPar}` : overPar} vs par {savedResult.par_total}</p>
          </Card>
          <Card className="p-4 text-center bg-amber-50 border-amber-200">
            <p className="text-xs text-neutral-600 mb-1">Stableford Net</p>
            <p className="text-3xl font-bold text-amber-700">{savedResult.total_stableford_net}</p>
            <p className="text-xs text-neutral-500 mt-1">points</p>
          </Card>
        </div>

        {/* Lesson suggestion — Popup modale */}
        {savedResult.lesson_suggestion?.should_suggest && showLessonModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowLessonModal(false)}
            data-testid="scorecard-lesson-modal"
          >
            <Card
              className="max-w-md w-full p-6 bg-gradient-to-br from-orange-50 via-white to-red-50 border-orange-300 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                  <Sparkles className="text-orange-600" size={28} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-center mb-2">Envie de progresser ?</h3>
              <p className="text-sm text-neutral-700 text-center mb-4 leading-relaxed">
                Tu es à <strong>+{savedResult.lesson_suggestion.over_par}</strong> du par. Un cours peut vraiment débloquer 5-10 coups en quelques séances.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowLessonModal(false)}
                  data-testid="scorecard-dismiss-lesson-btn"
                >
                  J'ai compris
                </Button>
                <Button
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => { setShowLessonModal(false); onNavigateToLessons && onNavigateToLessons(); }}
                  data-testid="scorecard-book-lesson-btn"
                >
                  Réserver un cours <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Détail trou par trou */}
        <details className="bg-white rounded-lg border p-4">
          <summary className="cursor-pointer font-semibold text-sm">Détail trou par trou</summary>
          <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
            <div className="font-bold">Trou</div>
            <div className="font-bold">Brut</div>
            <div className="font-bold">Rendus</div>
            <div className="font-bold">Net</div>
            {savedResult.holes.map(h => (
              <React.Fragment key={h.number}>
                <div>{h.number} (par {h.par})</div>
                <div>{h.gross}</div>
                <div className="text-emerald-600">+{h.strokes_received}</div>
                <div className="font-semibold">{h.net}</div>
              </React.Fragment>
            ))}
          </div>
        </details>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => setView('home')} data-testid="scorecard-back-home">Retour</Button>
          <Button onClick={() => setView('leaderboard')} className="bg-amber-500 hover:bg-amber-600"><Trophy size={14} className="mr-1" /> Voir le leaderboard</Button>
        </div>
      </div>
    );
  }

  // ==================== VIEW: LEADERBOARD ====================
  if (view === 'leaderboard') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Trophy className="text-amber-500" /> Leaderboard</h2>
          <button onClick={() => setView('home')} className="text-neutral-500 hover:text-neutral-700"><X size={20} /></button>
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 bg-neutral-100 rounded-full p-1">
            {['gross', 'net'].map(m => (
              <button
                key={m}
                onClick={() => setLeaderboardMode(m)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${leaderboardMode === m ? 'bg-emerald-600 text-white' : 'text-neutral-600 hover:bg-neutral-200'}`}
                data-testid={`leaderboard-mode-${m}`}
              >
                {m === 'net' ? 'Score Net' : 'Score Brut'}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-neutral-100 rounded-full p-1">
            {['month', 'year', 'all'].map(p => (
              <button
                key={p}
                onClick={() => setLeaderboardPeriod(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${leaderboardPeriod === p ? 'bg-emerald-600 text-white' : 'text-neutral-600 hover:bg-neutral-200'}`}
                data-testid={`leaderboard-period-${p}`}
              >
                {p === 'month' ? 'Mois' : p === 'year' ? 'Année' : 'Tout'}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-0 overflow-hidden">
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 px-4 py-2 bg-neutral-50 text-[10px] font-bold text-neutral-500 border-b">
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-2 sm:col-span-2">Joueur</div>
            <div className="col-span-1 hidden sm:block">Index</div>
            <div className="col-span-1 hidden sm:block">Tee</div>
            <div className="col-span-1 text-center">Date</div>
            <div className="col-span-1 text-right">
              {leaderboardMode === 'net' ? 'Net' : 'Brut'}
            </div>
          </div>
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center text-neutral-500 text-sm">Aucun score publié sur cette période</div>
          ) : leaderboard.map((sc, i) => (
            <div key={sc.id} className="grid grid-cols-6 sm:grid-cols-8 gap-2 px-4 py-3 border-b border-neutral-100 items-center text-sm">
              <div className="col-span-1 text-center font-bold text-lg">
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
              </div>
              <div className="col-span-2 sm:col-span-2 font-semibold">{sc.client_name || 'Anonyme'}</div>
              <div className="col-span-1 hidden sm:block text-xs text-neutral-500">{sc.handicap_index}</div>
              <div className="col-span-1 hidden sm:block text-xs text-neutral-500">{sc.tee_label}</div>
              <div className="col-span-1 text-center text-xs text-neutral-500">{sc.played_at}</div>
              <div className="col-span-1 text-right font-bold">
                {leaderboardMode === 'net' ? sc.total_net : sc.total_gross}
              </div>
            </div>
          ))}
        </Card>
      </div>
    );
  }

  // ==================== VIEW: HISTORY ====================
  if (view === 'history') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Mes parties</h2>
          <button onClick={() => setView('home')} className="text-neutral-500 hover:text-neutral-700"><X size={20} /></button>
        </div>
        {myScorecards.length === 0 ? (
          <p className="text-neutral-500 text-sm text-center py-8">Tu n'as encore enregistré aucune partie.</p>
        ) : (
          <div className="space-y-2">
            {myScorecards.map(sc => (
              <ScorecardRow key={sc.id} sc={sc} onDelete={deleteScorecard} onTogglePublish={togglePublish} onEdit={startEdit} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}

// ============================================================================
// ScorecardRow — Une ligne d'historique
// ============================================================================

function ScorecardRow({ sc, onDelete, onTogglePublish, onEdit }) {
  const overPar = sc.total_net - sc.par_total;
  return (
    <Card className="p-3 flex items-center gap-3 hover:shadow-sm transition">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm truncate">{sc.tee_label} · Idx {sc.handicap_index}</p>
          {sc.is_published && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Publié</span>}
        </div>
        <p className="text-xs text-neutral-500">{sc.played_at} · PH {sc.playing_handicap}</p>
      </div>
      <div className="text-right text-xs">
        <p><span className="text-neutral-500">Brut</span> <strong>{sc.total_gross}</strong></p>
        <p><span className="text-neutral-500">Net</span> <strong className={overPar > 10 ? 'text-orange-600' : 'text-emerald-700'}>{sc.total_net}</strong> ({overPar >= 0 ? '+' : ''}{overPar})</p>
        <p><span className="text-neutral-500">Stbl.</span> <strong className="text-amber-600">{sc.total_stableford_net}pts</strong></p>
      </div>
      <div className="flex flex-col gap-1">
        <button onClick={() => onEdit(sc)} className="text-[10px] text-blue-600 hover:underline" data-testid={`scorecard-edit-${sc.id}`}>
          Modifier
        </button>
        <button onClick={() => onTogglePublish(sc.id, sc.is_published)} className="text-[10px] text-emerald-600 hover:underline" data-testid={`scorecard-toggle-publish-${sc.id}`}>
          {sc.is_published ? 'Retirer' : 'Publier'}
        </button>
        <button onClick={() => onDelete(sc.id)} className="text-[10px] text-red-500 hover:underline" data-testid={`scorecard-delete-${sc.id}`}>
          Supprimer
        </button>
      </div>
    </Card>
  );
}

function stablefordNet(net, par) {
  const diff = net - par;
  if (diff <= -2) return 4;
  if (diff === -1) return 3;
  if (diff === 0) return 2;
  if (diff === 1) return 1;
  return 0;
}
